import { useContext } from 'react';
import type { ConnectedFacilityRateHistory } from '../../../js/api/models/ConnectedFacilityRateHistory';
import { SessionContext } from '../../../js/app/contexts/SessionContext';
import { useCodeDefinitionsAsMaps } from '../hooks/useCodeDefinitions';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './table';

type ConnectionRateHistoryTableProps = {
  rateHistory: ConnectedFacilityRateHistory[];
};

export function ConnectionRateHistoryTable({ rateHistory }: ConnectionRateHistoryTableProps) {
  const { audienceIsProvider } = useContext(SessionContext);
  const isProvider = audienceIsProvider ?? false;
  const ratePrefix = isProvider ? 'shift_rate' : 'facility_rate';

  const { data: codeDefinitionMaps } = useCodeDefinitionsAsMaps(['RateChangeReason']);
  const rateChangeReasons = codeDefinitionMaps?.RateChangeReason || {};

  // Helper function to get rate value based on user type
  function getRateValue(rate: ConnectedFacilityRateHistory, rateType: string): number | null {
    const key = `${ratePrefix}_${rateType}` as keyof ConnectedFacilityRateHistory;
    const value = rate[key];
    return typeof value === 'number' && value > 0 ? value : null;
  }

  // Helper function to get rate types that exist in the history
  function getRateTypes(rateHistory: ConnectedFacilityRateHistory[]): string[] {
    const types = ['day', 'night', 'call', '24h'];
    return types.filter((type) =>
      rateHistory.some((rate) => {
        const key = `${ratePrefix}_${type}` as keyof ConnectedFacilityRateHistory;
        return rate[key] !== null;
      })
    );
  }

  const rateTypes = getRateTypes(rateHistory);

  if (!rateHistory || rateHistory.length === 0) {
    return (
      <div className="tw:text-sm tw:text-zinc-500 tw:dark:text-zinc-400">
        No rate history available.
      </div>
    );
  }

  return (
    <Table compact>
      <TableHead>
        <TableRow noPointer className="tw:hover:bg-transparent">
          <TableHeader>
            <span className="tw:hidden tw:sm:inline">Effective </span>Date
          </TableHeader>
          {rateTypes.map((type) => (
            <TableHeader key={type} className="tw:capitalize">
              {type}
            </TableHeader>
          ))}
          <TableHeader>Reason</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {rateHistory.map((rate, index) => (
          <TableRow
            key={index}
            noPointer
            className="tw:!border-b tw:!border-zinc-200 tw:!border-collapse"
          >
            <TableCell className="tw:whitespace-nowrap">
              {rate.effective_at ? new Date(rate.effective_at).toLocaleDateString() : '--'}
            </TableCell>
            {rateTypes.map((type) => {
              const rateValue = getRateValue(rate, type);
              return (
                <TableCell key={type} className="tw:whitespace-nowrap tw:text-center">
                  {rateValue !== null ? `$${rateValue.toLocaleString()}` : '--'}
                </TableCell>
              );
            })}
            <TableCell className="tw:capitalize">
              {rate.reason_code
                ? rateChangeReasons?.[rate.reason_code] || rate.reason_code?.replace(/_/g, ' ')
                : '--'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
